import {
  faChevronUp, faSearch, faSort,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  RoundButtonComponent, RoundButtonSize,
} from '~/_shared/baseComponents/buttons/roundButton/roundButton.component';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import {
  DropdownComponent, type DropdownOption, DropdownPlacement, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import {
  InputSize, TextInputComponent,
} from '~/_shared/baseComponents/inputs';
import { LinkComponent } from '~/_shared/baseComponents/links';
import { HeightTransitionComponent } from '~/_shared/components/heightTransition/heightTransition.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { OverlayWrapperComponent } from '~/_shared/components/overlay/overlayWrapper.component';
import { PaginationComponent } from '~/_shared/components/pagination/pagination.component';
import { OverlayScrollbarComponent } from '~/_shared/components/scrollbar/overlayScrollbar.component';
import { ScrollbarType } from '~/_shared/components/scrollbar/scrollbar.component';
import { type Pagination } from '~/_shared/types/pagination/pagination';
import { useTranslation } from '~/_shared/utils/hooks';
import { useWindowHeight } from '~/_shared/utils/hooks/useWindowHeight';
import { clamp } from '~/_shared/utils/number/number.helpers';
import {
  dropdownStyle, headerButtonsStyle, headerButtonStyle, headerStyle, listHeaderStyle, listingItemIconStyle,
  listingItemStyle, listingItemTitleStyle, listingItemTriggerStyle, listingNumberStyle, listStyle, loaderStyle,
  noResultsStyle, searchLabelStyle, SecondaryListingItem, secondaryListingSpacer, sortDropdownContainerStyle,
  spaceTopStyle, toggleButtonIconStyle, toggleButtonStyle,
} from '~/map/dropdown/mapDropdown.styles';
import { MapDropdownSubItemComponent } from '~/map/dropdown/mapDropdownSubItem.component';
import { type MapListingItemWithPath } from '~/map/listing/item/mapListingItem';
import type { MapListingRowItem } from '~/map/listing/tableData/mapRow/mapRow.component';
import { useMapInfoDataSelector } from '~/store/mapInfo/mapInfo.selectors';
import { useUserIdSelector } from '~/store/userData/userData.selectors';
import {
  getSortOptions, type MapListSortType,
} from '../listing/mapListing.helpers';
import { getMapRowIcon } from '../listing/tableData/mapRow/mapRow.helpers';
import { compareIdToParentMapOrMapId } from '../map.helpers';

type MapDropdownProps = Readonly<{
  isLoading: boolean;
  isOpen: boolean;
  mapListingItems: ReadonlyArray<MapListingItemWithPath>;
  pagination: Pagination | null;
  searchQuery: string;
  sortType?: MapListSortType;
  title: string;
  toggledMapIds: ReadonlyArray<number>;
  triggerComponent: ReactNode;

  onClose: () => void;
  onPageChange: (page: number) => void;
  onSearchQueryChange?: (query: string) => void;
  onSortTypeChange?: (sortType: MapListSortType) => void;
  onToggledMapIdsChange?: (ids: number[]) => void;
}>;

export const MapDropdownComponent: FC<MapDropdownProps> = (props) => {
  const windowHeight = useWindowHeight();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(true);
  const [t] = useTranslation();
  const mapInfo = useMapInfoDataSelector();
  const currentUserId = useUserIdSelector();

  const sortOptions: DropdownOption<MapListSortType>[] = useMemo(() => getSortOptions(t), [t]);

  const toggleMap = (mapId: number) => {
    if (props.onToggledMapIdsChange) {
      const newSelectedMapIds = props.toggledMapIds.includes(mapId)
        ? props.toggledMapIds.filter(id => id !== mapId)
        : [...props.toggledMapIds, mapId];

      props.onToggledMapIdsChange(newSelectedMapIds);
    }
  };

  const onListingItemClick = () => {
    props.onClose();
  };

  const dropdownListViewportMaxHeight = windowHeight - 300;
  const dropdownListMaxHeight = clamp(dropdownListViewportMaxHeight, { min: 50, max: 280 });

  const toggleSearch = useCallback(() => {
    setIsSearchOpen(s => !s);
  }, []);
  const toggleSort = useCallback(() => setIsSortOpen(s => !s), []);

  const triggerButtonStyle = (isMenuOpen: boolean) =>
    isMenuOpen ? RoundButtonStyle.Quaternary : RoundButtonStyle.Primary;

  const toggleMapIdsLookup = new Set(props.toggledMapIds);

  useEffect(() => {
    if (props.isOpen) {
      return;
    }

    setIsSearchOpen(props.searchQuery !== '');
  }, [props.searchQuery, props.isOpen]);

  return (
    <DropdownComponent
      isOpen={props.isOpen}
      onClose={props.onClose}
      triggerComponent={props.triggerComponent}
      css={dropdownStyle}
      menuStyle={listStyle}
      closeOnInsideClick={false}
      dropdownPlacement={DropdownPlacement.BottomEnd}
      inPortal
    >
      <div css={{ width: '468px' }}>
        <div css={headerStyle}>
          <div css={headerButtonsStyle}>
            {props.onSearchQueryChange && (
              <RoundButtonComponent
                buttonStyle={triggerButtonStyle(isSearchOpen)}
                css={headerButtonStyle}
                icon={faSearch}
                onClick={toggleSearch}
                size={RoundButtonSize.Medium}
                tooltipLabel={t('Search')}
              />
            )}
            {props.onSortTypeChange && (
              <RoundButtonComponent
                buttonStyle={triggerButtonStyle(isSortOpen)}
                css={headerButtonStyle}
                icon={faSort}
                onClick={toggleSort}
                size={RoundButtonSize.Medium}
                tooltipLabel={t('Sort')}
              />
            )}
          </div>
          {isSearchOpen && props.onSearchQueryChange && (
            <div css={spaceTopStyle}>
              <label css={searchLabelStyle}>
                <TextInputComponent
                  type="text"
                  onChange={props.onSearchQueryChange}
                  value={props.searchQuery}
                  placeholder={t('Search Your Maps')}
                  icon={faSearch}
                  size={InputSize.Large}
                />
              </label>
            </div>
          )}

          {isSortOpen && props.onSortTypeChange && (
            <div css={spaceTopStyle}>
              <RegularDropdownComponent
                css={sortDropdownContainerStyle}
                inPortal
                inputSize={InputSize.Large}
                onChange={props.onSortTypeChange}
                options={sortOptions}
                prefixTriggerIcon={faSort}
                value={props.sortType}
              />
            </div>
          )}
        </div>

        <OverlayWrapperComponent
          minHeight={80}
        >
          <div css={listHeaderStyle}>{props.title}</div>
          <OverlayScrollbarComponent
            type={ScrollbarType.Vertical}
            maxHeight={dropdownListMaxHeight}
          >
            {props.mapListingItems.map((item, index) => {
              const isToggled = toggleMapIdsLookup.has(item.id);
              const isItemActive = compareIdToParentMapOrMapId(item.id, mapInfo);
              const snapshotCount = item.snapshots.length;
              const baseMaps = item.layering?.baseMaps;
              const baseMapsCount = (item.layering?.connected && baseMaps?.length) || 0;
              const subMapsCount = snapshotCount + baseMapsCount;

              return (
                <div
                  css={listingItemStyle}
                  key={item.id}
                >
                  <div css={listingItemTriggerStyle}>
                    <LinkComponent
                      path={item.path}
                      css={listingItemTitleStyle(isItemActive)}
                      onClick={onListingItemClick}
                    >
                      <span css={listingNumberStyle}>{index + 1}.</span>
                      <span css={listingItemIconStyle(isItemActive)}>
                        <FontAwesomeIcon icon={getMapRowIcon({ isSnapshot: false, item })} />
                      </span>
                      {item.name}
                    </LinkComponent>

                    {subMapsCount > 0 && (
                      <button
                        onClick={() => toggleMap(item.id)}
                        css={toggleButtonStyle}
                      >
                        <FontAwesomeIcon
                          css={toggleButtonIconStyle({ shouldRotate: isToggled })}
                          icon={faChevronUp}
                        />
                      </button>
                    )}
                  </div>

                  <HeightTransitionComponent animateOnMount>
                    {isToggled && (
                      <div>
                        {item.layering?.connected && baseMaps?.map((baseMap, i) => {
                          const hasAccess = baseMap.sharedUsers.some(user => user.id === currentUserId);
                          const baseMapItem: MapListingRowItem = {
                            ...baseMap,
                            isBaseLayer: true,
                            noAccess: !hasAccess,
                            snapshots: [],
                          };

                          return (
                            <MapDropdownSubItemComponent
                              index={i}
                              isSnapshot={false}
                              item={baseMapItem}
                              key={baseMapItem.id}
                              onListingItemClick={onListingItemClick}
                            />
                          );
                        })}

                        {item.snapshots.map((snapshot, i) => (
                          <MapDropdownSubItemComponent
                            key={snapshot.id}
                            index={i + baseMapsCount} // Continue lettering from base maps
                            isSnapshot
                            item={snapshot}
                            onListingItemClick={onListingItemClick}
                          />
                        ))}
                        {/* last item in the snapshots listing should have the opposite background
                        to actual last snapshot, that's why SecondaryListingItem is used
                        as it gets the alternate background for even and odd items */}
                        <SecondaryListingItem css={secondaryListingSpacer} />
                      </div>
                    )}
                  </HeightTransitionComponent>
                </div>
              );
            })}
          </OverlayScrollbarComponent>

          {(!props.isLoading && props.mapListingItems.length === 0) &&
            <p css={noResultsStyle}>{t('No results')}</p>
          }

          {props.pagination && (
            <PaginationComponent
              currentPage={props.pagination.currentPage}
              pageCount={props.pagination.lastPage}
              onPageSelect={props.onPageChange}
            />
          )}

          {props.isLoading && (
            <OverlayLoaderComponent
              css={loaderStyle}
              loaderSize={45}
            />
          )}
        </OverlayWrapperComponent>
      </div>
    </DropdownComponent>
  );
};
